import {classNames, computed, IDisposer, React} from '@api'
import {getFontFace, getFontSize, getFontStyle, getTextAlign, TDShape, TDShapeUtil, TldrawApp} from '@modules/whiteboard'
import {TLComponentProps} from '@tldraw/core'
import {action} from 'mobx'

export class ShapeComponentStore<T extends TDShape, U extends TDShapeUtil<T>> {
  constructor(public props: TLComponentProps<T> & {app: TldrawApp}, overrideStore?: any) {
    const {app: {shapeComponentStores}, shape: {id}} = props
    shapeComponentStores[id] = overrideStore ?? this
  }

  get shape() { return this.props.shape}
  get shapeStyle() { return this.shape.style}

  get app() { return this.props.app}
  @computed get id() {
    const {shape: {id, type}} = this
    return `${id}_${type}_svg`
  }

  _toDispose: Array<IDisposer> = []

  registerEffects = () => {
    return () => {
      const {app: {shapeComponentStores}, shape: {id}} = this
      delete shapeComponentStores[id]
      this._toDispose.forEach(d => d())
    }
  }

  get utils() {
    return this.app.shapeUtilFor<T>(this.shape) as U
  }

  @computed get dataAttributes() {
    const {props: {isGhost, isSelected, isEditing}} = this
    return {
      'data-ghost': isGhost ? true : undefined,
      'data-selected': isSelected ? true : undefined,
      'data-editing': isEditing ? true : undefined
    }
  }
  @computed get isResizing() {
    const {props: {isSelected}, app} = this
    return isSelected && app.isInSession && app.appState.status === 'transforming'
  }

  @computed get font() {
    return getFontStyle(this.shape.style)
  }

  @computed get bounds() {
    const {shape, utils: {getBounds}} = this
    return getBounds(shape)
  }

  get optionalTitle() {
    return this.shape.optionalTitle
  }

  @computed get titleTextDecoration() {
    const {shape: {optionalTitle, style: {fontStyle}}} = this
    if (!optionalTitle) return undefined
    return classNames(fontStyle?.underline && 'underline', fontStyle?.strikethrough && 'line-through')
  }

  @computed get titleFontWeight() {
    const {shape: {optionalTitle, style}} = this
    if (!optionalTitle) return undefined
    return style.fontStyle?.bold ? 'bold' : 'normal'
  }

  @computed get titleFontStyle() {
    const {shape: {optionalTitle, style}} = this
    if (!optionalTitle) return undefined

    return style.fontStyle?.italic ? 'italic' : undefined
  }

  @computed get titleFontFace() {
    const {shape: {optionalTitle, style}} = this
    if (!optionalTitle) return undefined

    return getFontFace(style.font)
  }

  @computed get titleCssVars(): any {
    const {optionalTitle, shape: {style}, titleFontFace, titleFontWeight, titleFontStyle, titleTextDecoration} = this

    if (!optionalTitle?.show) return null

    return {
      '--font-family': titleFontFace,
      '--text-color': style.textColor,
      '--font-size': getFontSize(style),
      '--text-align': getTextAlign(style.textAlign),
      '--font-weight': titleFontWeight,
      '--text-decoration': titleTextDecoration,
      '--font-style': titleFontStyle,
      '--background': style.backgroundColor
    }
  }

  @action onChangeOptionalTitle = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.optionalTitle.title = e.currentTarget.value
  }
}

export const ShapeComponentStoreContext = React.createContext<ShapeComponentStore<any, any>>(null)
export const useShapeComponentStore = () => React.useContext(ShapeComponentStoreContext)



// WEBPACK FOOTER //
// ./src/modules/whiteboard/shapes/ShapeComponentStore.tsx